import React, { useState } from 'react';
import { User } from '@apply/store/src/user/types';
import { isMobileScreen, ConfirmV2 } from '@apply/ui';
import { useNavigate } from 'react-router-dom';
import { IconFlyoutMenuButton, FlyoutDirection, IconButtonSize, IconButtonTheme } from '@apply/components';
import { useAuthentication } from '../../../Login/hooks/useAuthentication';
import { faUserCircle } from '@fortawesome/free-regular-svg-icons';
import { faUsers, faUsersCog } from '@fortawesome/free-solid-svg-icons';
import { faSignOut, faBars, faMedal } from '@fortawesome/free-solid-svg-icons';
import { logApplicationInsightsTrace } from '../../../common/functions/applicationInsightFunctions';
import { FeatureType } from '@apply/constants';
import { ProfileMenuOption, ProfileMenuSize } from './components/ProfileMenuOption';
import { useAuthenticationStore } from '../../../state/authenticationStore';

const mobileScreen = isMobileScreen();

export const ProfileButton: React.FC<{ user: User; printMode?: boolean }> = ({ user, printMode = false }) => {
    const selectedAuthorizedDrawUserEntity = useAuthenticationStore((s) => s.selectedEntity);
    const navigate = useNavigate();
    const { logout, isFeatureEnabled } = useAuthentication();
    const [isConfirmOpen, set_isConfirmOpen] = useState(false);

    const onConfirmLogout = () => {
        const stackTrace = Error().stack;
        logApplicationInsightsTrace('onConfirmLogout', { stackTrace });
        logout();
        set_isConfirmOpen(false);
        navigate('/login');
    };

    const onCancelLogout = () => {
        set_isConfirmOpen(false);
    };

    const handleLogout = () => {
        set_isConfirmOpen(true);
    };

    const handleSwitchUser = () => {
        navigate('/entity-selection');
    };

    const handleManageUsers = () => {
        navigate('/manageusers');
    };

    const handleExperience = () => {
        navigate('/experience');
    };

    const renderProfileButton = (printMode?: boolean) => {
        if (!printMode) {
            if (!mobileScreen) {
                return (
                    <IconFlyoutMenuButton
                        id='profileButton'
                        direction={FlyoutDirection.Left}
                        theme={IconButtonTheme.White}
                        size={IconButtonSize.Medium}
                        icon={faUserCircle}
                    >
                        <ul style={{ textAlign: 'left', display: 'grid', gap: '20px' }}>
                            <ProfileMenuOption
                                onClick={handleSwitchUser}
                                icon={faUsers}
                                visible={isFeatureEnabled(FeatureType.GeneralContractor) && user?.isGeneralContractor}
                                menuSize={ProfileMenuSize.Regular}
                            >
                                Switch User
                            </ProfileMenuOption>
                            <ProfileMenuOption
                                onClick={handleLogout}
                                icon={faSignOut}
                                menuSize={ProfileMenuSize.Regular}
                            >
                                Logout
                            </ProfileMenuOption>
                        </ul>
                    </IconFlyoutMenuButton>
                );
            } else {
                return (
                    <IconFlyoutMenuButton
                        id='menuButton'
                        direction={FlyoutDirection.Left}
                        theme={IconButtonTheme.Transparent}
                        size={IconButtonSize.Small}
                        icon={faBars}
                    >
                        <ul style={{ textAlign: 'left', display: 'grid', gap: '10px' }}>
                            <ProfileMenuOption
                                onClick={handleExperience}
                                icon={faMedal}
                                visible={user?.isRegistered && !selectedAuthorizedDrawUserEntity}
                                menuSize={ProfileMenuSize.Condensed}
                            >
                                Experience
                            </ProfileMenuOption>
                            <ProfileMenuOption
                                onClick={handleManageUsers}
                                icon={faUsersCog}
                                visible={
                                    isFeatureEnabled(FeatureType.GeneralContractor) &&
                                    user?.isRegistered &&
                                    !selectedAuthorizedDrawUserEntity
                                }
                                menuSize={ProfileMenuSize.Condensed}
                            >
                                Manage Users
                            </ProfileMenuOption>
                            <ProfileMenuOption
                                onClick={handleSwitchUser}
                                icon={faUsers}
                                visible={isFeatureEnabled(FeatureType.GeneralContractor) && user?.isGeneralContractor}
                                menuSize={ProfileMenuSize.Condensed}
                            >
                                Switch User
                            </ProfileMenuOption>
                            <ProfileMenuOption
                                onClick={handleLogout}
                                icon={faSignOut}
                                menuSize={ProfileMenuSize.Condensed}
                            >
                                Logout
                            </ProfileMenuOption>
                        </ul>
                    </IconFlyoutMenuButton>
                );
            }
        }
    };

    return (
        <>
            {renderProfileButton(printMode)}
            <ConfirmV2
                id='confirmLogoutModal'
                open={isConfirmOpen}
                title='Confirm Logout'
                onConfirm={onConfirmLogout}
                onCancel={onCancelLogout}
                okLabel='Yes'
                cancelLabel='No'
                width='340px'
                height='200px'
            >
                Warning: Unsaved work may be lost
            </ConfirmV2>
        </>
    );
};
